/**
 * IgnoreDialog — POST /intake/:id/ignore (plan §4.4: "Ignore is recorded, not
 * deleted"; §13.4 WP-31 frontend scope: "ignore asks for a reason").
 */
import { useState, FormEvent } from 'react';
import { Modal, primaryBtnCls, secondaryBtnCls, fieldCls, useToast } from '../../../ui';
import { ignoreIntakeItem } from '../../api/intake';
import type { IntakeItem } from '../../api/intake';

interface Props {
  item: IntakeItem;
  onClose: () => void;
  onIgnored: (item: IntakeItem) => void;
}

export function IgnoreDialog({ item, onClose, onIgnored }: Props) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const updated = await ignoreIntakeItem(item.id, reason.trim());
      toast(`${item.drive_name} ignored.`, { type: 'success' });
      onIgnored(updated);
    } catch (err) {
      const res = (err as { response?: { data?: { error?: string } } })?.response;
      setFormError(res?.data?.error ?? 'Something went wrong ignoring this item.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`Ignore — ${item.drive_name}`} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {formError}
          </div>
        )}
        <div>
          <label htmlFor="cta-ignore-reason" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Reason
          </label>
          <textarea
            id="cta-ignore-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className={`${fieldCls} w-full`}
            autoFocus
          />
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="submit" data-testid="cta-ignore-submit" disabled={!reason.trim() || submitting} className={primaryBtnCls}>
            {submitting ? 'Ignoring…' : 'Ignore'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default IgnoreDialog;
